import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  BoxProps,
  Divider,
  Flex,
  Link,
  List,
  ListItem,
  Text,
} from "@chakra-ui/react";
import { FiExternalLink } from "@react-icons/all-files/fi/FiExternalLink";
import { Link as GatsbyLink } from "gatsby";
import { SIDEBAR_OPACITY_ANIMATION_DURATION } from "../templates/learn";
import { ReactComponent as TocIcon } from "../images/icons/toc-icon.svg";
import { activeAfterStyles } from "./Nav";

type TableOfContentsProps = {
  items: {
    learnTocNodes: Queries.TableOfContentsQuery["learnMdx"]["nodes"];
    archTocNodes: Queries.TableOfContentsQuery["archMdx"]["nodes"];
  };
  pathname: string;
  opacity?: number;
  width?: string;
};

type TocSectionProps = {
  nodes: Queries.TableOfContentsQuery["learnMdx"]["nodes"];
  pathname: string;
  title: string;
  activeId: string | null;
};

const LINK_PROPS = {
  fontWeight: "600",
  mb: "8px",
  partiallyActive: true,
};

export const TableOfContentsIcon = (props: BoxProps) => {
  return (
    <Box
      borderColor="gray.300"
      borderWidth="1px"
      borderStyle="solid"
      borderRadius="6px"
      p="1"
      color="gray.900"
      cursor="pointer"
      zIndex={9}
      sx={{
        svg: {
          stroke: "gray.900",
        },
      }}
      _hover={{
        svg: {
          stroke: "purple.500",
        },
      }}
      {...props}
    >
      <TocIcon />
    </Box>
  );
};

const TableOfContents = ({
  items: { learnTocNodes = [], archTocNodes = [] },
  pathname,
  opacity,
  width,
}: TableOfContentsProps) => {
  const [activeId, setActiveId] = useState<string | null>(null);

  const allNodes = useMemo(
    () => [...learnTocNodes, ...archTocNodes],
    [learnTocNodes, archTocNodes]
  );
  useEffect(() => {
    const activeItem = allNodes.find((item) => item.fields?.slug === pathname);

    function handleScroll() {
      if (activeItem?.tableOfContents?.items) {
        const activeTableOfContentsItems = activeItem.tableOfContents.items as {
          url: string;
        }[];
        const halfway = window.innerHeight / 2;
        for (let i = activeTableOfContentsItems.length - 1; i >= 0; i--) {
          const { url } = activeTableOfContentsItems[i];
          const id = url.replace("#", "");
          const heading = document.getElementById(id);
          if (heading) {
            const { top } = heading.getBoundingClientRect();
            if (top <= halfway) {
              setActiveId(id);
              break;
            }
          }
        }
      }
    }
    window.addEventListener("scroll", handleScroll);
    // this is in place to trigger the scroll event on page load so the active class
    // is set initially
    window.scrollBy(0, 1);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [allNodes, pathname]);

  return (
    <Box
      opacity={opacity}
      width={width}
      transition={`ease-in-out ${SIDEBAR_OPACITY_ANIMATION_DURATION}ms all`}
    >
      <Flex alignItems="center" justifyContent="space-between" mb="6">
        <Text color="blue.900" fontWeight={"700"} fontSize={"1.3em"}>
          Table of contents
        </Text>
      </Flex>

      <List>
        <TocSection
          title="GraphQL Basics"
          nodes={learnTocNodes}
          pathname={pathname}
          activeId={activeId}
        />
      </List>

      <Divider borderColor="gray.300" mt="4" />
      <Box pb="4">
        <List>
          <TocSection
            title="GraphQL Architecture"
            nodes={archTocNodes}
            pathname={pathname}
            activeId={activeId}
          />
        </List>
      </Box>
      <Divider borderColor="gray.300" mb="4" />
      <Link
        onClick={() => {
          window.gtag?.("event", "graphos_toc_cta_click");
        }}
        isExternal
        display={"inline-block"}
        position="relative"
        {...LINK_PROPS}
        href="https://www.apollographql.com/docs/graphos/?utm_campaign=graphql.com"
        color={"blue.900"}
        role="group"
        _hover={{
          textDecoration: "none",
          color: "blue.900",
        }}
      >
        <Box display={"inline-flex"} alignItems="center">
          Try GraphOS &nbsp; <FiExternalLink />
        </Box>
        <Box
          _groupHover={{
            color: "blue.900",
          }}
          as="span"
          color="gray.500"
          pb="8px"
          fontWeight="400"
          fontSize="sm"
          display={"block"}
        >
          The GraphQL developer platform
        </Box>
      </Link>
    </Box>
  );
};

const TocSection = ({ activeId, nodes, pathname, title }: TocSectionProps) => {
  return (
    <>
      <Text color="gray.500" fontSize="12px" pt="4" pb="2">
        {title.toUpperCase()}
      </Text>
      {nodes.map((node, key) => {
        const { frontmatter, fields, tableOfContents } = node;
        const { title } = frontmatter ?? {};
        const { slug } = fields ?? {};
        const isActive = slug === pathname;
        if (!slug) {
          return null;
        }
        return (
          <ListItem py="4px" key={key}>
            <Link
              display={"inline-block"}
              position="relative"
              as={GatsbyLink}
              key={key}
              {...LINK_PROPS}
              to={slug}
              color={"blue.900"}
              _hover={{
                textDecoration: "none",
                color: "blue.900",
              }}
              _after={{
                ...(isActive ? activeAfterStyles : {}),
              }}
              onClick={() => {
                window.gtag?.("event", "toc_link_click", {
                  type: "heading",
                  link: slug,
                });
              }}
            >
              {title}
            </Link>
            {tableOfContents &&
              "items" in tableOfContents &&
              isActive &&
              (tableOfContents.items as { url: string; title: string }[]).map(
                (item) => {
                  const activeItem = activeId === item.url.replace("#", "");
                  return (
                    <Link
                      color={activeItem ? "purple.600" : "gray.500"}
                      pb="8px"
                      fontWeight={activeItem ? "500" : "400"}
                      display={"block"}
                      key={item.title}
                      href={item.url}
                      _hover={{
                        color: "blue.900",
                      }}
                      onClick={() => {
                        window.gtag?.("event", "toc_link_click", {
                          type: "subheading",
                          link: item.url,
                        });
                      }}
                    >
                      {item.title}
                    </Link>
                  );
                }
              )}
          </ListItem>
        );
      })}
    </>
  );
};

export default TableOfContents;
